import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from '@eon/i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LeafletMap,
  PageHeader,
  type LeafletPin,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Station } from '@eon/types';
import { StationMapPopup } from '@/components/StationMapPopup';
import {
  stationCoordinates,
  stationMapVariant,
} from '@/lib/station-map';

function buildMapPins(stations: Station[]): {
  pins: LeafletPin[];
  missing: Station[];
} {
  const pins: LeafletPin[] = [];
  const missing: Station[] = [];

  for (const station of stations) {
    const coords = stationCoordinates(station);
    if (!coords) {
      missing.push(station);
      continue;
    }
    pins.push({
      id: station.id,
      lat: coords.lat,
      lng: coords.lng,
      variant: stationMapVariant(station),
      popup: <StationMapPopup station={station} />,
    });
  }

  return { pins, missing };
}

export function MapPage() {
  const { t } = useTranslation();
  const list = hooks.useAdminStations({ page: 1, pageSize: 200 });
  const stations = list.data?.data ?? [];

  const { pins, missing } = useMemo(() => buildMapPins(stations), [stations]);

  return (
    <>
      <PageHeader
        title={t('nav.map')}
        description={t('map.description', {
          mapped: pins.length,
          total: stations.length,
        })}
      />

      {missing.length > 0 ? (
        <Card className="mb-4 border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {t('map.missingCoordinatesTitle', { count: missing.length })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {missing.map((station) => (
                <li key={station.id}>
                  <Link
                    to="/stations/$stationId/edit"
                    params={{ stationId: station.id }}
                    className="text-primary hover:underline"
                  >
                    {station.name}
                  </Link>
                  {station.address ? (
                    <span className="text-muted-foreground"> — {station.address}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <LeafletMap
            pins={pins}
            height={560}
            fitToPins
            className="overflow-hidden rounded-b-xl"
          />
        </CardContent>
      </Card>
    </>
  );
}
